import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useApp } from './store/useApp';
import { supabase } from './lib/supabase';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import { ActualizacionBanner } from './components/ActualizacionBanner';
import { limpiarMarcaRecuperacion } from './lib/appUpdate';
import { RequireAuth, RequireRole, RequireSession } from './components/Guards';
import { Loading } from './components/ui';

// Ruta crítica (primer render) eager; el resto se parte en chunks por ruta (React.lazy) para
// no servir un único bundle enorme por 4G dentro de una nave.
import Login from './pages/Login';
import Panel from './pages/Panel';
import Landing from './pages/Landing';
import Legal from './pages/Legal';
import Verify from './pages/Verify';
import CambiarPassword from './pages/CambiarPassword';

const Onboarding = lazy(() => import('./pages/Onboarding'));
const InventarioInicial = lazy(() => import('./pages/InventarioInicial'));
const CargaInicial = lazy(() => import('./pages/CargaInicial'));
const Entrada = lazy(() => import('./pages/Entrada'));
const Carga = lazy(() => import('./pages/Carga'));
const Pedidos = lazy(() => import('./pages/Pedidos'));
const Recepciones = lazy(() => import('./pages/Recepciones'));
const PedidosImportar = lazy(() => import('./pages/PedidosImportar'));
const Almacenes = lazy(() => import('./pages/Almacenes'));
const Mapa = lazy(() => import('./pages/Mapa'));
const Ubicaciones = lazy(() => import('./pages/Ubicaciones'));
const Etiquetas = lazy(() => import('./pages/Etiquetas'));
const Escanear = lazy(() => import('./pages/Escanear'));
const Ubicacion = lazy(() => import('./pages/Ubicacion'));
const Lpn = lazy(() => import('./pages/Lpn'));
const Buscar = lazy(() => import('./pages/Buscar'));
const Existencias = lazy(() => import('./pages/Existencias'));
const Traspaso = lazy(() => import('./pages/Traspaso'));
const Articulo = lazy(() => import('./pages/Articulo'));
const Movimiento = lazy(() => import('./pages/Movimiento'));
const Movimientos = lazy(() => import('./pages/Movimientos'));
const Auditoria = lazy(() => import('./pages/Auditoria'));
const Retirada = lazy(() => import('./pages/Retirada'));
const Recuento = lazy(() => import('./pages/Recuento'));
const Politicas = lazy(() => import('./pages/Politicas'));
const Pendientes = lazy(() => import('./pages/Pendientes'));
const Ajustes = lazy(() => import('./pages/Ajustes'));
const Admin = lazy(() => import('./pages/Admin'));
const Tutoriales = lazy(() => import('./pages/Tutoriales'));
const Tutorial = lazy(() => import('./pages/Tutorial'));

const ADMIN = ['owner', 'admin'];
const OP = ['owner', 'admin', 'operario'];

function Protegida({ children }) {
  const { debeCambiarPassword: debe, session, cargandoSesion, cargandoTenants, tenantActivo, esSuperadmin } = useApp();
  // Bloque A: contraseña temporal -> bloquea CUALQUIER ruta hasta cambiarla.
  if (debe) return <Navigate to="/cambiar-password" replace />;
  if (cargandoSesion) return <Loading />;
  if (!session) return <Navigate to="/login" replace />;
  // Un superadmin puede no tener empresa propia y aun así debe poder entrar (a /admin).
  if (!tenantActivo && !esSuperadmin) {
    if (cargandoTenants) return <Loading />;
    return <Navigate to="/login" replace />;
  }
  return <Layout>{children}</Layout>;
}

function Home() {
  const { session, cargandoSesion, tenantActivo, esSuperadmin } = useApp();
  if (cargandoSesion) return <Loading />;
  if (!session) {
    if (typeof window !== 'undefined') {
      const { hostname, search, hash } = window.location;
      // Confirmación de correo / invitación / recuperación / OAuth: dejar que Supabase procese el token en la URL.
      // NUNCA redirigir a la landing de venta si hay un token o código de autenticación presente.
      const autenticacionEnCurso = /(access_token|refresh_token|provider_token|[?&#](type|code|token_hash|error|error_description)=)/.test((hash || '') + (search || ''));
      // Si el CTA trae intención (?m=signup / ?m=...): al FORMULARIO de alta/entrada, no a la landing de venta.
      const sp = new URLSearchParams(search || '');
      if (sp.get('m')) return <Navigate to={`/login${search}`} replace />;
      // Redirección a la landing de venta SOLO en la raíz limpia, sin token ni intención. En preview se ve la Landing.
      if (hostname === 'hueco.getagentik.co' && !autenticacionEnCurso) {
        window.location.replace('https://getagentik.co/hueco');
        return <Loading />;
      }
    }
    return <Landing />;
  }
  if (!tenantActivo && esSuperadmin) return <Navigate to="/admin" replace />;
  return <Protegida><Panel /></Protegida>;
}

export default function App() {
  const { cargarSesion, cargarTenants, aplicarTemaInicial, cargandoSesion } = useApp();

  useEffect(() => {
    aplicarTemaInicial();
    cargarSesion();
    // Si la app renderiza y se mantiene estable, permite futuras recuperaciones ante ChunkLoadError.
    const limpieza = setTimeout(limpiarMarcaRecuperacion, 8000);
    const { data: sub } = supabase.auth.onAuthStateChange((ev, session) => {
      useApp.setState({ session, user: session?.user || null });
      // El arranque lo maneja cargarSesion(): ignoramos INITIAL_SESSION para no duplicar toda la carga
      // (doble arranque). Reaccionamos solo al login real y a la salida; el resto (TOKEN_REFRESHED/
      // USER_UPDATED) solo refresca la sesión de arriba sin relanzar tenants ni catálogo.
      if (ev === 'SIGNED_IN') {
        useApp.getState().revisarCambioPassword();
        useApp.getState().cargarSuperadmin();
        cargarTenants();
      } else if (ev === 'SIGNED_OUT') {
        useApp.setState({ esSuperadmin: false, esSuperadminCargado: false, tenants: [], tenantActivo: null });
      }
    });
    return () => { clearTimeout(limpieza); sub.subscription.unsubscribe(); };
  }, [cargarSesion, cargarTenants, aplicarTemaInicial]);

  return (
    <ErrorBoundary>
      <ActualizacionBanner />
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={cargandoSesion ? <Loading /> : <Login />} />
          <Route path="/__verify" element={<Verify />} />
          <Route path="/terminos" element={<Legal tipo="terminos" />} />
          <Route path="/privacidad" element={<Legal tipo="privacidad" />} />
          <Route path="/cookies" element={<Legal tipo="cookies" />} />
          <Route path="/aviso-legal" element={<Legal tipo="aviso-legal" />} />
          <Route path="/" element={<Home />} />
          <Route path="/cambiar-password" element={<RequireSession><CambiarPassword /></RequireSession>} />
          <Route path="/admin" element={<Protegida><Admin /></Protegida>} />
          <Route path="/onboarding" element={<Protegida><RequireRole roles={ADMIN}><Onboarding /></RequireRole></Protegida>} />
          <Route path="/inventario" element={<Protegida><RequireRole roles={OP}><InventarioInicial /></RequireRole></Protegida>} />
          <Route path="/carga-inicial" element={<Protegida><RequireRole roles={ADMIN}><CargaInicial /></RequireRole></Protegida>} />
          <Route path="/entrada" element={<Protegida><RequireRole roles={OP}><Entrada /></RequireRole></Protegida>} />
          <Route path="/recepciones" element={<Protegida><RequireRole roles={OP}><Recepciones /></RequireRole></Protegida>} />
          <Route path="/recepciones/:id" element={<Protegida><RequireRole roles={OP}><Recepciones /></RequireRole></Protegida>} />
          <Route path="/pedidos/importar" element={<Protegida><RequireRole roles={ADMIN}><PedidosImportar /></RequireRole></Protegida>} />
          <Route path="/pedidos/:id/carga" element={<Protegida><RequireRole roles={OP}><Carga /></RequireRole></Protegida>} />
          <Route path="/pedidos" element={<Protegida><RequireRole roles={OP}><Pedidos /></RequireRole></Protegida>} />
          <Route path="/pedidos/:id" element={<Protegida><RequireRole roles={OP}><Pedidos /></RequireRole></Protegida>} />
          <Route path="/almacenes" element={<Protegida><RequireRole roles={ADMIN}><Almacenes /></RequireRole></Protegida>} />
          <Route path="/mapa" element={<Protegida><Mapa /></Protegida>} />
          <Route path="/mapa/:almacenId" element={<Protegida><Mapa /></Protegida>} />
          <Route path="/ubicaciones" element={<Protegida><Ubicaciones /></Protegida>} />
          <Route path="/etiquetas" element={<Protegida><RequireRole roles={ADMIN}><Etiquetas /></RequireRole></Protegida>} />
          <Route path="/escanear" element={<Protegida><RequireRole roles={OP}><Escanear /></RequireRole></Protegida>} />
          <Route path="/u/:codigo" element={<Protegida><Ubicacion /></Protegida>} />
          <Route path="/p/:lpn" element={<Protegida><Lpn /></Protegida>} />
          <Route path="/buscar" element={<Protegida><Buscar /></Protegida>} />
          <Route path="/existencias" element={<Protegida><Existencias /></Protegida>} />
          <Route path="/traspaso" element={<Protegida><RequireRole roles={OP}><Traspaso /></RequireRole></Protegida>} />
          <Route path="/articulo/:sku" element={<Protegida><Articulo /></Protegida>} />
          <Route path="/movimiento/:id" element={<Protegida><Movimiento /></Protegida>} />
          <Route path="/movimientos" element={<Protegida><RequireRole roles={OP}><Movimientos /></RequireRole></Protegida>} />
          <Route path="/auditoria" element={<Protegida><RequireRole roles={ADMIN}><Auditoria /></RequireRole></Protegida>} />
          <Route path="/retirada" element={<Protegida><RequireRole roles={ADMIN}><Retirada /></RequireRole></Protegida>} />
          <Route path="/recuento" element={<Protegida><RequireRole roles={OP}><Recuento /></RequireRole></Protegida>} />
          <Route path="/politicas" element={<Protegida><RequireRole roles={ADMIN}><Politicas /></RequireRole></Protegida>} />
          <Route path="/pendientes" element={<Protegida><RequireRole roles={OP}><Pendientes /></RequireRole></Protegida>} />
          <Route path="/ajustes" element={<Protegida><RequireRole roles={ADMIN}><Ajustes /></RequireRole></Protegida>} />
          <Route path="/tutoriales" element={<Protegida><Tutoriales /></Protegida>} />
          <Route path="/tutoriales/:id" element={<Protegida><Tutorial /></Protegida>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
